import { AppointmentType } from "../hooks"
import { appointmentTypeCopy } from "../preferencesCopy"
import {
  Box,
  GenderAffirmingCareIcon,
  IconWithBackground,
  NotebookPenIcon,
  Radio,
  SelectedIcon,
  Typography
} from "@carrotfertility/carotene-core"
import type { IconWithBackgroundColor, IconWithBackgroundProps } from "@carrotfertility/carotene-core"
import React from "react"

// PLACEHOLDER ICONS (FEWG-138): the design calls for a heart (initial) and a clipboard
// (follow-up), neither of which exists in carotene yet. GenderAffirmingCareIcon and
// NotebookPenIcon stand in until the real icons are added to carotene and swapped here.
const APPOINTMENT_TYPE_ICON: Record<AppointmentType["kind"], IconWithBackgroundProps["icon"]> = {
  initial: GenderAffirmingCareIcon,
  follow_up: NotebookPenIcon
}

const APPOINTMENT_TYPE_ICON_COLOR: Record<AppointmentType["kind"], IconWithBackgroundColor> = {
  initial: "primary",
  follow_up: "blue"
}

// The circular selection control: an outline circle when unselected, a filled check when selected.
const unselectedIndicator = (
  <Box
    sx={(theme) => ({
      inlineSize: 22,
      blockSize: 22,
      borderRadius: "50%",
      border: `1px solid ${theme.palette.divider}`
    })}
  />
)

const selectedIndicator = (
  <Box
    sx={(theme) => ({
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      inlineSize: 22,
      blockSize: 22,
      borderRadius: "50%",
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white
    })}
  >
    <SelectedIcon sx={{ fontSize: 14 }} />
  </Box>
)

type VisitTypeCardProps = {
  type: AppointmentType
  // The wiring FormRadioGroup hands each option (value/checked/disabled/onChange); spread onto the Radio.
  radioProps: React.ComponentProps<typeof Radio>
}

// A selectable visit-type card: icon chip + selection indicator on top, then title + description.
// Rendered as a <label> wrapping the real Radio so the whole card is an accessible click target.
export function VisitTypeCard({ type, radioProps }: VisitTypeCardProps): JSX.Element {
  const { title, description } = appointmentTypeCopy(type.kind)
  const selected = Boolean(radioProps.checked)
  const disabled = Boolean(radioProps.disabled)
  const inputId = `appointment-type-${type.id}`

  return (
    <Box
      component="label"
      htmlFor={inputId}
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(theme.tokens.spacing.sm),
        blockSize: "100%",
        boxSizing: "border-box",
        padding: theme.spacing(theme.tokens.spacing.md),
        border: `${theme.tokens.borderWidth.md} solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
        borderRadius: theme.tokens.borderRadius.md,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1
      })}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <IconWithBackground
          color={APPOINTMENT_TYPE_ICON_COLOR[type.kind]}
          icon={APPOINTMENT_TYPE_ICON[type.kind]}
          size="small"
        />
        <Radio
          {...radioProps}
          id={inputId}
          sx={{ padding: 0 }}
          icon={unselectedIndicator}
          checkedIcon={selectedIndicator}
        />
      </Box>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.xxs)
        })}
      >
        <Typography variant="h5" component="span">
          {title}
        </Typography>
        <Typography color="text.secondary">{description}</Typography>
      </Box>
    </Box>
  )
}
